import { keepPreviousData } from "@tanstack/react-query";
import { CommitmentActivityTableStats } from "features/commitments/CommitmentActivityStats";
import { useCapitalActivityTableDataRetrieve } from "gen/endpoints/capital-activity/capital-activity";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import React from "react";
import { useTranslation } from "react-i18next";
import { LoadingState } from "shared/components/empty/LoadingState";
import { FundSelectButton } from "shared/components/input/select/FundSelectButton";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import {
  NonNullBooleanParam,
  StringArrayParam,
} from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";

export interface CommitmentActivityProps {
  fundFamilyId?: string;
  investorId?: string;
  investorEntityId?: string;
  commitmentId?: string;
}

export const CommitmentActivity: React.FC<CommitmentActivityProps> = ({
  fundFamilyId,
  investorId,
  investorEntityId,
  commitmentId,
}) => {
  const { t } = useTranslation([
    "commitment",
    "investorEntity",
    "investor",
    "common",
  ]);

  const { data: funds } = useFundListSuspense({
    investor_entity_id: investorEntityId,
    investor_id: investorId,
  });

  const [fundIds, setFundIds] = useQueryParam("fundId", StringArrayParam);
  const [isEqualizedView, setIsEqualizedView] = useQueryParam(
    "equalized",
    NonNullBooleanParam,
  );

  // Need to differentiate between fundIds being None (first load), and wanting to include
  // all funds, and case no filters are chosen
  const invalidFundId: string[] = ["invalid"];

  const { data: tableData, isFetching } = useCapitalActivityTableDataRetrieve(
    {
      fund_ids:
        fundIds === null
          ? undefined
          : fundIds.length == 0
            ? invalidFundId
            : fundIds,
      fund_family_ids: fundFamilyId ? [fundFamilyId] : undefined,
      investor_id: investorId,
      investor_entity_id: investorEntityId,
      commitment_id: commitmentId,
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const emptyTitle = commitmentId
    ? t("commitment:tabs.activity.table.empty.title")
    : investorEntityId
      ? t("investorEntity:tabs.activity.table.empty.title")
      : t("investor:tabs.activity.table.empty.title");
  const emptyDescription = commitmentId
    ? t("commitment:tabs.activity.table.empty.description")
    : investorEntityId
      ? t("investorEntity:tabs.activity.table.empty.description")
      : t("investor:tabs.activity.table.empty.description");

  return (
    <PageTabBody>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("common:capitalActivity")}</TableBoxTitle>
          <DataTableBoxActions>
            {!commitmentId && funds.length > 1 && (
              <FundSelectButton
                funds={funds}
                selectedFundIds={fundIds}
                onSelectedFundIdsChange={setFundIds}
              />
            )}
            <EqualizedViewToggleButton
              isEqualizedView={isEqualizedView}
              setIsEqualizedView={setIsEqualizedView}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <CommitmentActivityTableStats
          fundFamilyId={fundFamilyId}
          fundIds={fundIds}
          investorId={investorId}
          investorEntityId={investorEntityId}
          commitmentId={commitmentId}
        />
        <TableBoxBody>
          {tableData ? (
            <DataTable
              data={tableData}
              isLoading={isFetching}
              emptyStateProps={{
                title: emptyTitle,
                description: emptyDescription,
              }}
            />
          ) : (
            <LoadingState />
          )}
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
